import { AxiosInstance } from 'axios';

import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import { getAddressDetails } from '@/graphql/queries/getAddressDetails';
import { searchAddress } from '@/graphql/queries/searchAddress';
import { formatAddress } from '@/lib/utils';
import {
  AddressLookup,
  FormattedAddress,
  NullableAddressLookup,
  OrderServiceAddress,
  ReferenceAddress,
  StructuredAddress,
  T_AddressLookUpDetail,
} from '@/types';

export class AddressService {
  private readonly addressAgent: AxiosInstance;
  private readonly gqlAgent: ApolloClient<NormalizedCacheObject>;
  constructor(addressAgent: AxiosInstance, gqlAgent: ApolloClient<NormalizedCacheObject>) {
    this.addressAgent = addressAgent;
    this.gqlAgent = gqlAgent;
  }
  public getAddress = async (term: string, type: string) =>
    (
      await this.addressAgent.get<AddressLookup[]>('/lookup?', {
        params: {
          term,
          type,
        },
      })
    ).data;

  public getAddressDetail = async (id: string) => {
    let detail = (
      await this.addressAgent.get<T_AddressLookUpDetail>('/search?', {
        params: {
          id,
        },
      })
    ).data;

    return new AddressDetail(detail);
  };

  public getNzPostAddressDetails = async (id: string) => {
    const res = await this.gqlAgent.query({
      query: getAddressDetails,
      variables: { id },
      context: { skipAuth: true },
    });
    return res.data.getAddressDetails;
  };

  public searchNzPostAddress = async (variables: { query: string; count: number }) => {
    const res = await this.gqlAgent.query({
      query: searchAddress,
      variables,
      context: { skipAuth: true },
    });
    return res.data.searchAddress;
  };

  public getServiceAddress = async (
    address: NullableAddressLookup
  ): Promise<[OrderServiceAddress, FormattedAddress]> => {
    if (!address) {
      throw new Error('Address cannot be null');
    }

    const installationAddressDetails = await this.getAddressDetail(address.id);

    // incorrect type of address -- needs to be physical and returns a tui
    if (!installationAddressDetails.references) {
      throw new Error('No address found');
    }

    if (!installationAddressDetails.references.tui) {
      throw new Error('Incorrect type of address provided');
    }

    const { structured_address: structuredAddress } = installationAddressDetails;
    const installationAddressFormatted = formatAddress(
      // Not all addresses have road_abv and returning null causes problems in ICOMS.
      structuredAddress.road_abv
        ? `${structuredAddress.street_number} ${structuredAddress.street_name} ${structuredAddress.road_abv}`
        : `${structuredAddress.street_number} ${structuredAddress.street_name}`,
      structuredAddress.suburb,
      structuredAddress.town || structuredAddress.region,
      structuredAddress.postcode,
      installationAddressDetails.references.tui
    );

    const serviceAddress: OrderServiceAddress = {
      street: installationAddressFormatted.line1,
      suburb: installationAddressFormatted.line2,
      city: installationAddressFormatted.line3,
      postCode: installationAddressFormatted.line4,
      tuiid: installationAddressDetails.references.tui,
    };

    return [serviceAddress, installationAddressFormatted];
  };
}

export class AddressDetail implements T_AddressLookUpDetail {
  formatted_address: FormattedAddress;
  structured_address: StructuredAddress;
  references: ReferenceAddress;
  formatted_delivery_address: FormattedAddress;

  constructor(source: T_AddressLookUpDetail) {
    this.formatted_address = source.formatted_address;
    this.structured_address = source.structured_address;
    this.references = source.references;

    if (source.formatted_address.line1.match(/^PO BOX/)) {
      this.formatted_delivery_address = {
        line1: source.formatted_address.line1,
        line2: source.formatted_address.line3,
        line3: source.formatted_address.line4,
        line4: source.structured_address.postcode,
        tui: source.references.tui!,
      };
    } else {
      this.formatted_delivery_address = {
        line1: `${source.structured_address.street_number} ${source.structured_address.street_name} ${source.structured_address.road_type}`,
        line2: source.structured_address.suburb,
        line3: source.structured_address.town,
        line4: source.structured_address.postcode,
        tui: source.references.tui!,
      };
    }
  }
}
